import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Briefcase, 
  Users, 
  Heart, 
  TrendingUp,
  ArrowRight,
  MapPin,
  Clock,
  Sparkles
} from 'lucide-react';

const Careers = () => {
  const values = [
    {
      icon: Users,
      title: 'Collaborative Culture',
      description: 'Work with talented, passionate people who care about making events memorable.'
    },
    {
      icon: TrendingUp,
      title: 'Growth Opportunities',
      description: 'Advance your career with learning opportunities, mentorship, and clear growth paths.'
    },
    {
      icon: Heart,
      title: 'Work-Life Balance',
      description: 'We believe in sustainable work practices and support flexible arrangements.'
    },
    {
      icon: Briefcase,
      title: 'Impact',
      description: 'Make a real difference by helping connect people with exceptional event experiences.'
    }
  ];

  const openPositions = [
    {
      title: 'Senior Full-Stack Developer',
      department: 'Engineering',
      location: 'Remote / Accra, Ghana',
      type: 'Full-time',
      description: 'Build and scale our platform using modern technologies. Lead technical initiatives and mentor junior developers.'
    },
    {
      title: 'Product Designer',
      department: 'Design',
      location: 'Remote / Accra, Ghana',
      type: 'Full-time',
      description: 'Design beautiful, intuitive experiences for our users. Work closely with product and engineering teams.'
    },
    {
      title: 'Customer Success Manager',
      department: 'Operations',
      location: 'Accra, Ghana',
      type: 'Full-time',
      description: 'Help our clients and vendors succeed on the platform. Build relationships and drive satisfaction.'
    },
    {
      title: 'Marketing Specialist',
      department: 'Marketing',
      location: 'Remote / Accra, Ghana',
      type: 'Full-time',
      description: 'Develop and execute marketing strategies to grow our user base and brand awareness.'
    },
    {
      title: 'Business Development Representative',
      department: 'Sales',
      location: 'Accra, Ghana',
      type: 'Full-time',
      description: 'Onboard new vendors and expand our marketplace. Build partnerships and drive growth.'
    },
    {
      title: 'Data Analyst',
      department: 'Analytics',
      location: 'Remote',
      type: 'Full-time',
      description: 'Analyze platform data to drive insights and inform strategic decisions across the business.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 to-white border-b border-gray-100">
        <div className="page-container py-12 md:py-20 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 rounded-full text-sm font-light mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Join Our Team</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Build Your Career
              <span className="block mt-2 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                With Us
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              We're building the future of event planning. Join a team that's passionate about 
              connecting people with exceptional experiences and making events memorable.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#open-positions"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-xl font-light hover:bg-gray-800 transition-colors"
              >
                View Open Positions
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-light hover:border-gray-400 transition-colors"
              >
                Learn About Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Why Work Here Section */}
      <div className="py-12 md:py-20 bg-white">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Work With Us
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We're building something special, and we want you to be part of it.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div 
                    key={index}
                    className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300 text-center"
                  >
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-900 text-white rounded-xl mb-4">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-12 md:py-20 bg-gray-50">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Benefits & Perks
              </h2>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200">
              <div className="grid sm:grid-cols-2 gap-6">
                {[
                  'Competitive salary and equity',
                  'Health insurance coverage',
                  'Flexible work arrangements',
                  'Professional development budget',
                  'Generous paid time off',
                  'Remote work options',
                  'Team building activities',
                  'Learning and growth opportunities'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center mt-0.5">
                      <span className="text-xs">✓</span>
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Open Positions Section */}
      <div id="open-positions" className="py-12 md:py-20 bg-white">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Open Positions
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We're always looking for talented individuals to join our growing team.
              </p>
            </div>

            <div className="space-y-4">
              {openPositions.map((position, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {position.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {position.department}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {position.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {position.type}
                        </span>
                      </div>
                      <p className="text-gray-600 leading-relaxed">
                        {position.description}
                      </p>
                    </div>
                    <button className="flex-shrink-0 px-6 py-2 bg-black text-white rounded-lg font-light hover:bg-gray-800 transition-colors whitespace-nowrap">
                      Apply Now
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-gray-600 mb-4">
                Don't see a position that matches your skills?
              </p>
              <button className="inline-flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-light hover:border-gray-400 transition-colors">
                Send Us Your Resume
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-12 md:py-20 bg-gray-900 text-white">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Join Us?
            </h2>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              We're excited to hear from you. Apply today and help us shape the future of event planning.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#open-positions"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-black rounded-xl font-light hover:bg-gray-100 transition-colors"
              >
                View Open Positions
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white text-white rounded-xl font-light hover:bg-white/10 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Careers;

